/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
} from '@gh/builder-shared';
import { AppRouter, getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { RouteDataService, SlicedDataSource } from '@gh/core-data';
import { cursorTracker, newGridPaginationCursor } from '@gh/core-ui-grid';
import { SubOfficeListBusinessService } from '../../resources/sub-office-list-business.service';
import { SubOfficeListItem } from '../../resources/sub-office-list-item.model';
import { SubOfficeListGridDefinition } from './sub-office-list-grid-definition.service';
import { SubOfficeStatus, SystemRoleBit, UserType } from '@gh/builder-shared-data';
import { DialogService, NotificationService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';

@Component({
  standalone: false,
  selector: 'gh-sub-office-list',
  templateUrl: 'sub-office-list.component.html',
  viewProviders: [SubOfficeListGridDefinition],
})
export class SubOfficeListComponent implements OnInit {
  offices: SlicedDataSource<SubOfficeListItem>;
  dataSource: SlicedDataSource<SubOfficeListItem>;
  userType = UserType;
  cursor = newGridPaginationCursor();
  SubOfficeStatus = SubOfficeStatus;

  get isCanCreate(): boolean {
    return !this.userContext.corporateView  && (this.userContext.hasSystemRole(SystemRoleBit.Corporate)
           || this.userContext.hasSystemRole(SystemRoleBit.Master));
  }

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private routeData: RouteDataService,
              private navigationService: BuilderNavigationService,
              private businessService: SubOfficeListBusinessService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private dialogService: DialogService,
              private notifications: NotificationService,
              public userContext: BuilderUserContext,
              public gridDefinition: SubOfficeListGridDefinition) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);
    this.gridDefinition.bind(gridLayout);
    const tracker = this.routeData.tracker().withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(() => this.loadData());
  }

  reload(): void {
    this.loadData();
  }

  loadData(): void {
    this.dataSource = this.businessService.findAllAsDataSource();
  }

  onCreate(): void {
    this.router.navigateToChild(['/setup/sub-offices/new']);
  }

  onEdit(office: SubOfficeListItem): void {
      this.router.navigateToChild(['/setup/sub-offices', office.subOfficeId]);
  }

  onDeactivate(office: SubOfficeListItem): void {
    const flow = this.pipelines.toggleItem({
      question: 'msg_office_deactivation',
      successMessage: { id: 'msg_office_deactivated_successfully', params: { name: office.subOfficeName } },
      execute: this.commonActions.execute((state: CommonFlowState<SubOfficeListItem>) =>
        this.businessService.deactivate(state.value)),
      finish: () => this.loadData(),
    });

    flow.run({
      value: office,
    });
  }

  onDelete(office: SubOfficeListItem): void {
    this.dialogService.confirmation(
      yesNoConfirmation('msg_sub_office_deletion')).pipe(
      filter$(yesAnswer)).subscribe(() => {
      this.businessService.delete(office).subscribe(() => {
        this.notifications.success('msg_office_deleted_successfully', { name: office.subOfficeName });
        this.loadData();
      },
                                                    this.notifications.processAll,
      );
    });
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    const params = {
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'SUB_OFFICE',
      label: label ? label : 'lbl_sub_offices_list',
    };
    if (!entityId) {
      params['eventTypeIds'] =  [259, 260, 261, 264];
    }
    this.navigationService.goToAel(params);
  }
}
